// Sample test plans bundled with the SIT UI. Designed to teach the plan
// shape and give users a one-click starting point for common scenarios.
// Each template renders to YAML in the Templates tile; the user copies,
// downloads, or sends to the Run Trigger / Plan Editor with one click.

export const TEMPLATES = [
  {
    id: 'hello-world',
    name: 'Hello world — single prompt + hangup',
    category: 'Smoke test',
    difficulty: 'Beginner',
    description:
      "Simplest possible test. Dial in, wait for the IVR's greeting, hang up. Use this to confirm your destination DID answers and Polly TTS works.",
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: hello-world-smoke
  name: Hello world — single prompt
target:
  ccaas: wxcc
  destination: "+10000000000"  # set in Run Trigger
  fromDid: "+18042032195"
steps:
  - id: wait-greeting
    expectPrompt:
      timeoutSec: 5
      match:
        substring: welcome
  - id: end
    hangup: {}
`
  },
  {
    id: 'touch-tone-menu',
    name: 'Touch-tone menu — press 1, expect confirmation',
    category: 'IVR menu',
    difficulty: 'Beginner',
    description:
      'Single-level menu. Hear "Press 1 for X", press 1, expect a confirmation prompt, hang up. Default 5s pauses are right for deterministic touch-tone IVRs.',
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: touch-tone-press-1
  name: Touch-tone menu — press 1
target:
  ccaas: wxcc
  destination: "+10000000000"
  fromDid: "+18042032195"
steps:
  - id: wait-menu
    expectPrompt:
      timeoutSec: 5
      match:
        substring: press 1
  - id: press-1
    pressDtmf:
      digits: "1"
      signaling: rfc2833
  - id: wait-confirmation
    expectPrompt:
      timeoutSec: 5
      match:
        semantic:
          intent: confirmation that the caller selected option 1
          tolerance: medium
  - id: end
    hangup: {}
`
  },
  {
    id: 'multi-menu',
    name: 'Multi-level menu — drill into a sub-menu',
    category: 'IVR menu',
    difficulty: 'Intermediate',
    description:
      "Two-level menu navigation. Listen → press 1 → next menu → press 2 → confirmation → hang up. Tests that DTMF presses chain correctly across menu levels.",
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: multi-menu-1-then-2
  name: Multi-level menu — 1 then 2
target:
  ccaas: wxcc
  destination: "+10000000000"
  fromDid: "+18042032195"
steps:
  - id: wait-main-menu
    expectPrompt:
      timeoutSec: 5
      match:
        substring: main menu
  - id: press-1
    pressDtmf: { digits: "1", signaling: rfc2833 }
  - id: wait-submenu
    expectPrompt:
      timeoutSec: 5
      match:
        semantic:
          intent: announcement of sub-menu options
          tolerance: medium
  - id: press-2
    pressDtmf: { digits: "2", signaling: rfc2833 }
  - id: wait-confirmation
    expectPrompt:
      timeoutSec: 5
      match:
        semantic:
          intent: confirmation of selection
          tolerance: medium
  - id: end
    hangup: {}
`
  },
  {
    id: 'transfer-to-queue',
    name: 'Press 2 → expect transfer to a named queue',
    category: 'Routing',
    difficulty: 'Intermediate',
    description:
      'Validates a queue routing path. Press 2, expect to hear "transferring you" / queue music. The 30s expectTransfer default covers ringback + initial queue greeting.',
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: press-2-transfer-billing
  name: Press 2 → Billing queue
target:
  ccaas: wxcc
  destination: "+10000000000"
  fromDid: "+18042032195"
steps:
  - id: wait-menu
    expectPrompt:
      timeoutSec: 5
      match:
        substring: press 2
  - id: press-2
    pressDtmf: { digits: "2", signaling: rfc2833 }
  - id: confirm-transfer
    expectTransfer:
      timeoutSec: 30
      to:
        queueName: Billing
      allowPromptTextEvidence: true
  - id: end
    hangup: {}
`
  },
  {
    id: 'iva-conversation',
    name: 'IVA conversation — question-and-answer with an AI agent',
    category: 'IVA / AI agent',
    difficulty: 'Advanced',
    description:
      "Full conversational test against a Lex bot / IVA. Press 1, wait 25s for IVA welcome (cold-start LLM latency is real), speak your request, answer the IVA's follow-up questions. Use this as a template for any AI agent test — replace the saySpeech text with your real test inputs.",
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: iva-patient-lookup
  name: IVA — patient record lookup
target:
  ccaas: wxcc
  destination: "+10000000000"
  fromDid: "+18042032195"
steps:
  - id: wait-main-menu
    expectPrompt:
      timeoutSec: 5
      match:
        substring: press 1
  - id: press-1
    pressDtmf: { digits: "1", signaling: rfc2833 }
  - id: wait-iva-welcome
    expectPrompt:
      timeoutSec: 25  # IVAs commonly take 15-30s to greet
      match:
        semantic:
          intent: AI agent greeting
          tolerance: medium
  - id: speak-request
    saySpeech:
      text: I would like to review my patient record please
  - id: wait-iva-asks-name
    expectPrompt:
      timeoutSec: 25
      match:
        semantic:
          intent: IVA asks for patient name or identifier
          tolerance: medium
  - id: speak-name
    saySpeech:
      text: Camila Lopez
  - id: wait-iva-asks-dob
    expectPrompt:
      timeoutSec: 25
      match:
        semantic:
          intent: IVA asks for date of birth or identifier
          tolerance: medium
  - id: speak-dob
    saySpeech:
      text: September 12, 1987
  - id: wait-iva-confirms
    expectPrompt:
      timeoutSec: 25
      match:
        semantic:
          intent: IVA confirms patient record or provides information
          tolerance: high
  - id: end
    hangup: {}
`
  },
  {
    id: 'hold-music-only',
    name: 'After-hours / closed message — just listen',
    category: 'Smoke test',
    difficulty: 'Beginner',
    description:
      "Dial, listen for the after-hours / closed message, hang up. Useful for validating off-hours IVR configurations without entering any menu paths.",
    yaml: `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: after-hours-listen
  name: After-hours / closed message
target:
  ccaas: wxcc
  destination: "+10000000000"
  fromDid: "+18042032195"
steps:
  - id: wait-after-hours
    expectPrompt:
      timeoutSec: 10
      match:
        semantic:
          intent: closed / after-hours / try again later message
          tolerance: high
  - id: end
    hangup: {}
`
  }
];
